export type HierarchyTerm = 'site' | 'level' | 'zone' | 'tunnel' | 'team';

export interface TermLabel {
  singular: string;
  plural?: string;
}

export type HierarchyTermLabels = Record<HierarchyTerm, TermLabel>;

export const DEFAULT_TERM_LABELS: Record<HierarchyTerm, Required<TermLabel>> = {
  site: { singular: 'Site', plural: 'Sites' },
  level: { singular: 'Level', plural: 'Levels' },
  zone: { singular: 'Zone', plural: 'Zones' },
  tunnel: { singular: 'Tunnel', plural: 'Tunnels' },
  team: { singular: 'Team', plural: 'Teams' },
};

const NOT_THE_HIERARCHY = [
  /\b(?:sea|stock|risk|access|alert|log|severity|authority|security|noise|dust|water|gas|plan|role|priority|escalation|clearance|compliance|permission)\s+levels?\b/gi,
  /\bhierarchy levels?\b/gi,
  /\bon[- ]?site\b/gi,
  /\boff[- ]?site\b/gi,
  /\b(?:utm|time)\s+zone\b/gi,
  /\bteam\s+(?:members?|leads?|leaders?)\b/gi,
  /\b(?:it|support|emergency management|evacuation|transport|processing|platform|engineering|site)\s+team\b/gi,
];

const WORD = /\b(sites?|levels?|zones?|tunnels?|teams?)\b/gi;
const SHIELD = '\u0000';

export function mentionsHierarchyTerm(text: string): boolean {
  const meaningful = NOT_THE_HIERARCHY.reduce((rest, pattern) => rest.replace(pattern, ' '), text);
  return new RegExp(WORD.source, 'i').test(meaningful);
}

function pluralOf(label: TermLabel): string {
  const plural = label.plural?.trim();
  return plural === undefined || plural === '' ? `${label.singular}s` : plural;
}

function matchCase(source: string, label: string): string {
  if (source.length > 1 && source === source.toUpperCase()) return label.toUpperCase();
  const first = source.charAt(0);
  if (first === first.toLowerCase()) return label.toLowerCase();
  return label;
}

function fixArticle(before: string, nextWord: string): string {
  const article = /(^|[^A-Za-z])(a|an)(\s+)$/i.exec(before);
  if (!article) return before;
  const [matched, lead = '', current = '', space = ''] = article;
  const fixed = /^[aeiou]/i.test(nextWord) ? 'an' : 'a';
  if (current.toLowerCase() === fixed) return before;
  const startsUpper = current !== current.toLowerCase();
  const cased = startsUpper ? fixed.charAt(0).toUpperCase() + fixed.slice(1) : fixed;
  return before.slice(0, before.length - matched.length) + lead + cased + space;
}

export function translateHierarchyTerms(text: string, labels: HierarchyTermLabels): string {
  if (!text) return text;

  const shielded: string[] = [];
  const shield = (value: string) => {
    shielded.push(value);
    return `${SHIELD}${shielded.length - 1}${SHIELD}`;
  };

  let working = text;
  for (const pattern of NOT_THE_HIERARCHY) working = working.replace(pattern, shield);
  const chosenNames = Object.values(labels)
    .flatMap((label) => [label.singular, pluralOf(label)])
    .filter((name) => name.trim().length > 0)
    .sort((a, b) => b.length - a.length);
  for (const name of chosenNames) {
    const escaped = name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    working = working.replace(new RegExp(`\\b${escaped}\\b`, 'gi'), shield);
  }

  let output = '';
  let cursor = 0;
  for (const match of working.matchAll(WORD)) {
    const word = match[0];
    const term = word.toLowerCase().replace(/s$/, '') as HierarchyTerm;
    const label = labels[term];
    const isPlural = /s$/i.test(word) && word.toLowerCase() !== term;
    const replacement = matchCase(word, isPlural ? pluralOf(label) : label.singular);
    const preceding = working.slice(cursor, match.index);
    output += fixArticle(preceding, replacement) + replacement;
    cursor = match.index + word.length;
  }
  output += working.slice(cursor);

  return output.replace(new RegExp(`${SHIELD}(\\d+)${SHIELD}`, 'g'), (_, index: string) => shielded[Number(index)] ?? '');
}

export interface BorrowedName {
  term: HierarchyTerm;
  name: string;
  belongsTo: HierarchyTerm;
}

export function findBorrowedDefaultName(
  chosen: Partial<Record<HierarchyTerm, string>>,
): BorrowedName | null {
  for (const [term, value] of Object.entries(chosen) as [HierarchyTerm, string | undefined][]) {
    const name = value?.trim().toLowerCase();
    if (!name) continue;
    for (const [owner, defaults] of Object.entries(DEFAULT_TERM_LABELS) as [HierarchyTerm, Required<TermLabel>][]) {
      const isOwnDefault = owner === term;
      const matchesDefault = [defaults.singular, defaults.plural].some((word) => word.toLowerCase() === name);
      if (!isOwnDefault && matchesDefault) return { term, name: value?.trim() ?? name, belongsTo: owner };
    }
  }
  return null;
}
